// src/ui/CallDock.jsx — the right-side call panel.
//
// One dock for every call state — outgoing (nice gradient + avatar), incoming
// (accept / decline), active (timer + controls) — voice and video. It lives at the
// App level and SQUEEZES whatever is behind it (chats or Vibes) exactly the way the
// Starred/Search/Appearance panels squeeze the conversation: the `.call-open` class
// pads the shell by --call-w, and the dock occupies the freed strip.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { clampPos, snapPos, startDrag, readJSON, writeJSON } from './floating';
import Avatar from './Avatar';
/* 🟢 MicOn / MicOff / CamOff / HangIcon used to be defined right here as inline
   SVGs. They moved into the shared icon set when the guest call surface needed
   the same four — two copies of an icon is two icons that drift, and the mute
   button looking different in two places reads as two different features. */
import { Phone, Video, MicOn, MicOff, CamOff, HangIcon } from './icons';
import {
  acceptCall, rejectCall, endCall, toggleMute, toggleCamera,
  requestVideoUpgrade, acceptVideoUpgrade, declineVideoUpgrade,
} from '../services/calls';

function fmt(sec) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const pad = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m % 60)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
}

/** tiny inline icons the shared set doesn't have */
/* ── docked, floating, or out of the way ──────────────────────────────────
   🟥 THE CALL PANEL COULD ONLY EVER BE ONE THING: a strip pinned to the right,
      squeezing whatever you were reading. During a long call that is the whole
      screen rearranged around a face — and there was no way to move it, shrink
      it, or put it down without hanging up.

   🟢 THREE STATES, AND A WAY BACK FROM EACH. Docked is what it always was.
      Floating lifts it out of the layout so the chat goes back to full width and
      the window can be dragged anywhere. Tucked hides the window and leaves a
      pill — because a call you cannot see must still be a call you can find, and
      "close" that ends the call is not a close, it is a hang-up with a friendly
      name. */
const MODE_KEY = 'allo_call_dock_mode';
const POS_KEY = 'allo_call_dock_pos';
const FLOAT_W = 320;
const FLOAT_H = 420;
const PILL_W = 190;
const PILL_H = 56;

export default function CallDock({ call, chats, myUid }) {
  const [mode, setMode] = useState(() => {
    try {
      const v = localStorage.getItem(MODE_KEY);
      return ['docked', 'floating', 'tucked'].includes(v) ? v : 'docked';
    } catch { return 'docked'; }
  });
  const [pos, setPos] = useState(() => readJSON(POS_KEY, { x: null, y: null }));
  const posRef = useRef(pos);
  posRef.current = pos;
  const pick = useCallback((m) => {
    setMode(m);
    try { localStorage.setItem(MODE_KEY, m); } catch { /* private mode */ }
    /* 🟢 `storage` does not fire in the tab that wrote it, so the shell would keep
       its reserved strip until something else re-rendered. One event, dispatched
       here, is what tells App to release it now. */
    try { window.dispatchEvent(new Event('allo-call-mode')); } catch { /* older browser */ }
  }, []);

  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const localVideoRef = useRef(null);
  const [tick, setTick] = useState(0);

  // attach streams to the media elements
  useEffect(() => {
    if (remoteVideoRef.current && call.remoteStream) remoteVideoRef.current.srcObject = call.remoteStream;
    if (remoteAudioRef.current && call.remoteStream) remoteAudioRef.current.srcObject = call.remoteStream;
  }, [call.remoteStream, call.callType, call.status]);
  useEffect(() => {
    if (localVideoRef.current && call.localStream) localVideoRef.current.srcObject = call.localStream;
  }, [call.localStream, call.callType, call.status, call.cameraOff]);

  // 1s timer while active
  useEffect(() => {
    if (call.status !== 'active') return undefined;
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [call.status]);

  if (call.status === 'idle') return null;

  // resolve a display name/avatar from the chat list (callerData is the fallback)
  const chat = (chats || []).find((c) => !c.isGroup && (c.participants || []).includes(call.peerUid));
  const name = call.callerData?.fullName || call.callerData?.username
    || chat?.participantNames?.[call.peerUid] || 'Allo user';

  const isVideo = call.callType === 'video';
  const showVideo = isVideo && (call.status === 'active' || call.status === 'connecting' || call.status === 'outgoing');
  const elapsed = call.startedAt ? (Date.now() - call.startedAt) / 1000 + tick * 0 : 0;

  const statusLine = call.status === 'incoming'
    ? (isVideo ? 'Incoming video call…' : 'Incoming voice call…')
    : call.status === 'active'
      ? fmt(elapsed)
      : (call.note || 'Calling…');

  const floating = mode === 'floating';
  const tucked = mode === 'tucked';
  const boxW = tucked ? PILL_W : FLOAT_W;
  const boxH = tucked ? PILL_H : FLOAT_H;
  const at = clampPos(pos.x, pos.y, boxW, boxH);
  const onDragStart = (e) => startDrag(e, {
    getBox: () => clampPos(posRef.current.x, posRef.current.y, boxW, boxH),
    w: boxW,
    h: boxH,
    onMove: setPos,
    onEnd: () => setPos((p) => {
      const snapped = snapPos(p.x, p.y, boxW, boxH);
      writeJSON(POS_KEY, snapped);
      return snapped;
    }),
  });

  /* 🟢 TUCKED IS STILL A LIVE CALL, so the pill carries the timer and the two
     things you would reach for without looking: hang up, or bring it back. */
  if (tucked) {
    return (
      <div
        className="call-tuck"
        style={{ left: at.x, top: at.y, width: PILL_W, height: PILL_H }}
        onPointerDown={onDragStart}
      >
        <audio ref={remoteAudioRef} autoPlay />
        <span className="cp-dot" aria-hidden />
        <span className="cp-txt"><b>{name}</b><i>{statusLine}</i></span>
        <button className="cp-open" onClick={() => pick('floating')} title="Show the call" aria-label="Show the call">▣</button>
        <button className="cp-end" onClick={endCall} title="Hang up" aria-label="Hang up"><HangIcon width={15} height={15} /></button>
      </div>
    );
  }

  return (
    <div
      className={`call-dock ${isVideo ? 'video' : 'voice'} ${floating ? 'floating' : ''}`}
      style={floating ? { left: at.x, top: at.y, width: FLOAT_W, height: FLOAT_H } : undefined}
    >
      {/* 🟢 THE HANDLE IS ALSO THE MODE SWITCH. Dragging is only meaningful while
          floating, and startDrag ignores buttons, so the same strip is a grab
          area and a toolbar without the two fighting. */}
      <div className="cd-bar" onPointerDown={floating ? onDragStart : undefined}>
        {floating ? (
          <button className="cd-bar-btn" onClick={() => pick('docked')} title="Attach to the side panel" aria-label="Attach to the side panel">⇥</button>
        ) : (
          <button className="cd-bar-btn" onClick={() => pick('floating')} title="Detach — move it anywhere" aria-label="Detach the call window">⤢</button>
        )}
        <button className="cd-bar-btn" onClick={() => pick('tucked')} title="Tuck away (the call keeps running)" aria-label="Tuck the call away">–</button>
      </div>
      {/* remote media — audio always mounts so voice calls are audible */}
      <audio ref={remoteAudioRef} autoPlay />
      {showVideo && call.remoteStream && call.status === 'active' ? (
        <video className="cd-remote" ref={remoteVideoRef} autoPlay playsInline />
      ) : null}

      {/* self view — full during setup, corner tile once the call is up */}
      {showVideo && (
        <video
          /* 🟥 THE MIRROR WAS BAKED INTO .cd-local.full AND .cd-local.tile, so it
             applied to the REAR camera too — every sign and every face the wrong
             way round in your own preview. GuestCall.jsx already got this right
             (`call.facing !== 'environment'`); the dock did not. A mirror is right
             for the front lens, because you are looking at yourself, and wrong for
             the back one, which is a window onto the room. */
          className={`cd-local ${call.status === 'active' && call.remoteStream ? 'tile' : 'full'} ${call.cameraOff ? 'off' : ''} ${call.facing !== 'environment' ? 'mirror' : ''}`}
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
        />
      )}

      {/* voice backdrop / video pre-connect identity */}
      {(!showVideo || !call.remoteStream || call.status !== 'active') && (
        <div className="cd-id">
          <div className="cd-avatar">
            <Avatar chat={chat} peerUid={call.peerUid} size="lg" myUid={myUid}
              profile={call.callerData ? { profileImage: call.callerData.profileImage, fullName: name } : null} />
          </div>
          <b className="cd-name">{name}</b>
          <span className="cd-status">{statusLine}</span>
        </div>
      )}

      {/* active-call header strip (over video) */}
      {call.status === 'active' && showVideo && call.remoteStream && (
        <div className="cd-strip"><b>{name}</b><span>{fmt(elapsed)}</span></div>
      )}

      {/* 🟢 peer state chips — mirrors the app: "<name> is muted" / camera off */}
      {call.status === 'active' && (call.peerMuted || (isVideo && (call.peerCameraOff || call.peerVideoHeld))) && (
        <div className="cd-peerflags">
          {call.peerMuted && <span className="cd-peerflag"><MicOff width={13} height={13} /> {name} is muted</span>}
          {isVideo && call.peerCameraOff && <span className="cd-peerflag"><CamOff width={13} height={13} /> {name}&rsquo;s camera is off</span>}
          {isVideo && !call.peerCameraOff && call.peerVideoHeld && <span className="cd-peerflag"><CamOff width={13} height={13} /> Video paused · Poor connection</span>}
        </div>
      )}

      {/* the peer wants to turn this into a video call */}
      {call.upgradeAsk && (
        <div className="cd-upgrade">
          <span><Video width={15} height={15} /> {name} wants to switch to video</span>
          <div>
            <button className="cd-ok" onClick={acceptVideoUpgrade}>Turn on camera</button>
            <button className="cd-no" onClick={declineVideoUpgrade}>Keep voice</button>
          </div>
        </div>
      )}
      {call.upgradePending && (
        <div className="cd-upgrade"><span>Asking to switch to video…</span></div>
      )}

      {/* controls */}
      <div className="cd-controls">
        {call.status === 'incoming' ? (
          <>
            <button className="cd-btn accept" onClick={acceptCall} title="Answer">
              {isVideo ? <Video width={20} height={20} /> : <Phone width={20} height={20} />}
            </button>
            <button className="cd-btn hang" onClick={rejectCall} title="Decline"><HangIcon /></button>
          </>
        ) : (
          <>
            <button className={`cd-btn ${call.muted ? 'on' : ''}`} onClick={toggleMute} title={call.muted ? 'Unmute' : 'Mute'}>
              {call.muted ? <MicOff /> : <MicOn />}
            </button>
            {isVideo && (
              <button className={`cd-btn ${call.cameraOff ? 'on' : ''}`} onClick={toggleCamera} title={call.cameraOff ? 'Turn camera on' : 'Turn camera off'}>
                {call.cameraOff ? <CamOff /> : <Video width={18} height={18} />}
              </button>
            )}
            {!isVideo && call.status === 'active' && (
              <button className="cd-btn" onClick={requestVideoUpgrade} title="Switch to video call">
                <Video width={18} height={18} />
              </button>
            )}
            <button className="cd-btn hang" onClick={() => endCall()} title="Hang up"><HangIcon /></button>
          </>
        )}
      </div>
    </div>
  );
}
